// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Khipu panel in the headset (Khipu plan, slice 4;
 * `docs/AYNI_XR_KHIPU_REPLACES_TOURS_PLAN.md`). While a Khipu plays in the
 * Khipu player, or a lesson in the lesson player, the headset shows its
 * title, where it is (step or moment), the step's title, what it says
 * (captions and questions; a lesson's prompts for the students), its
 * dataset line, and Previous / Play-Pause / Next / Close. A step's quiz has
 * its answers as buttons (A to D): one press answers, and the right one is
 * then marked. A lesson step's real-time variables (2026-10-09) are buttons
 * too, numbered in the lesson's order: one press adds that stream over the
 * dataset (or takes it off), and what to look for joins the lines. In a
 * meeting's room it shows the presenter's step instead, read-only, without
 * buttons.
 *
 * Pictures, clips and links are not drawn: they come from other sites and
 * a page in another tab would end the immersive session. The panel says
 * how many there are, to see outside the headset.
 *
 * Drawn with the panel kit (`vrPanelKit`), like the dock and the side
 * panels; the host (`vrSession`) places it beside the globe and routes the
 * controller's presses (`vrInteraction`). Written from scratch for AYNI:
 * nothing here comes from TerraViz's headset code.
 */
import type * as THREE from 'three'
import { plural, t } from '../i18n'
import { createCanvasPanel, PANEL_COLORS, type CanvasPanel, type PanelPainter } from './vrPanelKit'

/** What the panel shows; a new object whenever any of it changes. */
export interface VrKhipuState {
  title: string
  /** "Story · Step 2 of 5". */
  progress: string
  /** The step's title, or ''. */
  heading: string
  /** The dataset line ("Loading …", "… is not in your plan"), or ''. */
  dataset: string
  /** Captions, questions and tasks, in order. */
  lines: readonly string[]
  /** Pictures, clips and links the step has (not drawn in the headset). */
  media: number
  canPrev: boolean
  isLast: boolean
  offersPlay: boolean
  playing: boolean
  /** The room's view of the presenter's Khipu: no buttons; the presenter moves it on. */
  readOnly?: boolean
  /** A step's quiz: its answers as buttons, which one was chosen, and (once chosen) the right one. */
  quiz?: { options: readonly string[]; chosen: number | null; correct: number }
  /** A lesson step's real-time variables, in order: whether each plays over the dataset, or is outside the plan. */
  variables?: ReadonlyArray<{ title: string; on: boolean; locked?: boolean }>
}

export type VrKhipuAction = 'prev' | 'next' | 'play' | 'close' | 'quiz-0' | 'quiz-1' | 'quiz-2' | 'quiz-3' | 'rt-0' | 'rt-1' | 'rt-2' | 'rt-3'

const QUIZ_ACTIONS: VrKhipuAction[] = ['quiz-0', 'quiz-1', 'quiz-2', 'quiz-3']
const RT_ACTIONS: VrKhipuAction[] = ['rt-0', 'rt-1', 'rt-2', 'rt-3']
const LETTERS = ['A', 'B', 'C', 'D'] // i18n-exempt: option letters
/** The answer buttons' row height. */
const QUIZ_H = 76

export interface VrKhipuPanelHandle {
  readonly mesh: THREE.Mesh
  setState(state: VrKhipuState | null): void
  isVisible(): boolean
  hitTest(uv: { x: number; y: number }): VrKhipuAction | null
  dispose(): void
}

/** The plane, in metres: about as wide as the side panel, half as tall. */
export const KHIPU_PANEL_WIDTH = 0.6
export const KHIPU_PANEL_HEIGHT = 0.42
const CANVAS_W = 1200
const CANVAS_H = 840
const PAD = 40
const BUTTON_H = 84
/** Most lines of text the panel draws; the rest is said to be there. */
const MAX_LINES = 7

/** Lines that fit `maxWidth`, words kept whole (a word too long for a line is cut). */
export function wrapText(measure: (s: string) => number, text: string, maxWidth: number): string[] {
  const out: string[] = []
  for (const paragraph of text.split('\n')) {
    let line = ''
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const next = line ? `${line} ${word}` : word
      if (measure(next) <= maxWidth) {
        line = next
        continue
      }
      if (line) out.push(line)
      line = word
      while (measure(line) > maxWidth && line.length > 1) {
        let cut = line.length - 1
        while (cut > 1 && measure(`${line.slice(0, cut)}…`) > maxWidth) cut--
        out.push(`${line.slice(0, cut)}…`)
        line = ''
        break
      }
    }
    if (line) out.push(line)
  }
  return out
}

export function drawKhipuPanel(p: PanelPainter<VrKhipuAction>, s: VrKhipuState): void {
  const { g } = p
  p.frame()
  const inner = p.w - PAD * 2
  // Head: title, where it is, and Close.
  p.text(s.title, PAD, PAD + 34, { size: 38, weight: 600, maxWidth: inner - 110 })
  p.text(s.progress, PAD, PAD + 82, { size: 28, color: PANEL_COLORS.muted, maxWidth: inner - 110 })
  if (!s.readOnly) p.button(p.w - PAD - 84, PAD, 84, 84, '', 'close', { icon: 'close' })
  let y = PAD + 132
  g.fillStyle = PANEL_COLORS.border
  g.fillRect(PAD, y - 22, inner, 2)
  if (s.heading) {
    p.text(s.heading, PAD, y + 20, { size: 34, weight: 600, maxWidth: inner })
    y += 56
  }
  if (s.dataset) {
    p.text(s.dataset, PAD, y + 16, { size: 28, color: PANEL_COLORS.textSecondary, maxWidth: inner })
    y += 46
  }
  g.font = p.font(30)
  const lines = s.lines.flatMap((l) => wrapText((x) => g.measureText(x).width, l, inner))
  const vars = s.readOnly ? [] : (s.variables ?? []).slice(0, RT_ACTIONS.length)
  const quizRows = (s.quiz ? Math.ceil(s.quiz.options.length / 2) : 0) + Math.ceil(vars.length / 2)
  const room = Math.min(MAX_LINES + (s.readOnly ? 2 : 0), Math.floor((p.h - PAD - (s.readOnly ? 0 : BUTTON_H) - quizRows * (QUIZ_H + 14) - 60 - y) / 40))
  for (const line of lines.slice(0, room)) {
    p.text(line, PAD, y + 18, { size: 30, maxWidth: inner })
    y += 40
  }
  const more = [lines.length > room ? t('vr.khipu.more') : '', s.media ? plural(s.media, { one: 'vr.khipu.media.one', other: 'vr.khipu.media.other' }) : ''].filter(Boolean).join(' · ')
  if (more) p.text(more, PAD, y + 22, { size: 26, color: PANEL_COLORS.muted, maxWidth: inner })
  if (s.readOnly) return
  // The step's real-time variables, then the quiz's answers, two to a row, over the navigation.
  const by = p.h - PAD - BUTTON_H
  const qw = (inner - 20) / 2
  vars.forEach((v, i) => {
    const row = Math.floor(i / 2)
    const vy = by - (quizRows - row) * (QUIZ_H + 14)
    p.button(PAD + (i % 2) * (qw + 20), vy, qw, QUIZ_H, `${i + 1}. ${v.title}`, RT_ACTIONS[i], {
      active: v.on,
      primary: false,
      ...(v.on ? { icon: 'check' as const } : {}),
      ...(v.locked && !v.on ? { disabled: true } : {}),
    })
  })
  if (s.quiz) {
    const q = s.quiz
    const varRows = Math.ceil(vars.length / 2)
    q.options.forEach((option, i) => {
      const row = varRows + Math.floor(i / 2)
      const qy = by - (quizRows - row) * (QUIZ_H + 14)
      const answered = q.chosen !== null
      p.button(PAD + (i % 2) * (qw + 20), qy, qw, QUIZ_H, `${LETTERS[i]}. ${option}`, QUIZ_ACTIONS[i], {
        disabled: answered && i !== q.correct && i !== q.chosen,
        active: answered && i === q.correct,
        primary: false,
        ...(answered && i === q.correct ? { icon: 'check' as const } : {}),
      })
    })
  }
  // Prev / Play-Pause / Next.
  const gap = 20
  const n = s.offersPlay ? 3 : 2
  const bw = (inner - gap * (n - 1)) / n
  let x = PAD
  p.button(x, by, bw, BUTTON_H, t('lessons.player.prev'), 'prev', { disabled: !s.canPrev, icon: 'chevron-left' })
  x += bw + gap
  if (s.offersPlay) {
    p.button(x, by, bw, BUTTON_H, t(s.playing ? 'khipu.player.pause' : 'khipu.player.play'), 'play', { icon: s.playing ? 'pause' : 'play', active: s.playing })
    x += bw + gap
  }
  p.button(x, by, bw, BUTTON_H, t(s.isLast ? 'lessons.player.finish' : 'khipu.player.next'), 'next', { primary: true, icon: s.isLast ? 'check' : 'chevron-right' })
}

export function createVrKhipuPanel(THREE_: typeof THREE): VrKhipuPanelHandle {
  const panel: CanvasPanel<VrKhipuAction> = createCanvasPanel(THREE_, {
    widthM: KHIPU_PANEL_WIDTH,
    heightM: KHIPU_PANEL_HEIGHT,
    canvasW: CANVAS_W,
    canvasH: CANVAS_H,
    renderOrder: 11,
  })
  panel.mesh.visible = false
  let shown: VrKhipuState | null = null
  return {
    mesh: panel.mesh,
    setState(state) {
      if (state === shown) return
      shown = state
      panel.mesh.visible = !!state
      if (state) panel.draw((p) => drawKhipuPanel(p, state))
    },
    isVisible: () => panel.mesh.visible,
    hitTest: (uv) => (panel.mesh.visible ? panel.hitTest(uv) : null),
    dispose: () => panel.dispose(),
  }
}
